<script setup>
import { VDataTableServer } from 'vuetify/labs/VDataTable'
import { useAbility } from '@casl/vue'

import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import DocumentService from '@/services/quality/document.service'
import { RetentionBasis, retentionBasisTitle, retentionBasisColor } from '@/enums/quality/retention-basis'
import { retentionTriggerTitle } from '@/enums/quality/retention-trigger'
import { retentionDispositionTitle, retentionDispositionColor } from '@/enums/quality/retention-disposition'
import { destructionMethodTitle } from '@/enums/quality/destruction-method'
import RetentionScheduleDrawer from '@/views/quality/document/retention/RetentionScheduleDrawer.vue'
import DestructionEventDrawer from '@/views/quality/document/retention/DestructionEventDrawer.vue'

definePage({
  meta: {
    name: 'app-quality-document-retention',
    subject: 'document.menu',
    action: 'retention',
  },
})

const { can } = useAbility()
const canManage = computed(() => can('manage_retention', 'quality.document'))
const canRecordDestruction = computed(() => can('record_destruction', 'quality.document'))

// ── Gap banner ───────────────────────────────────────
// RetentionScheduleService::gaps() — active, non-ARCHIVE_PERMANENT schedules
// with no destruction event inside the window. Not paginated (a plain
// resource collection with no `meta` key), so useApi's afterFetch interceptor
// (src/composables/useApi.js:84-86) already unwraps the `{ data: [...] }`
// envelope — data.value IS the array here, unlike the paginated schedule and
// event lists below where `.data` must still be read off a `{ data, meta }`
// envelope. Reading `data?.data` on this response silently produces
// `undefined` (arrays have no `.data`), which is exactly the bug this comment
// exists to prevent regressing to.
const gapYears = ref(1)
const gaps = ref([])
const isFetchingGaps = ref(false)

const fetchGaps = async () => {
  isFetchingGaps.value = true
  await DocumentService.retentionGaps(gapYears.value, data => {
    gaps.value = data ?? []
  }, () => {
    gaps.value = []
  })
  isFetchingGaps.value = false
}

watch(gapYears, fetchGaps)

// ── Schedules ────────────────────────────────────────
const searchQuery = ref('')

const debouncedSearch = useDebounceFn(value => {
  searchQuery.value = value
  page.value = 1
}, 500)

const basisFilter = ref(null)
const activeOnly = ref(false)

const basisOptions = Object.values(RetentionBasis).map(value => ({ title: retentionBasisTitle(value), value }))

const schedules = ref([])
const schedulesTotal = ref(0)
const isFetchingSchedules = ref(false)
const page = ref(1)
const itemsPerPage = ref(15)

const scheduleHeaders = [
  { title: 'Record Type', key: 'record_type', sortable: false },
  { title: 'Retention Period', key: 'retention_period_months', sortable: false },
  { title: 'Basis', key: 'retention_basis', sortable: false },
  { title: 'Trigger', key: 'trigger_event', sortable: false },
  { title: 'Disposition', key: 'disposition', sortable: false },
  { title: 'Owner', key: 'owner.name', sortable: false },
  { title: 'Events', key: 'destruction_events_count', sortable: false, align: 'center' },
  { title: '', key: 'actions', sortable: false, align: 'end' },
]

// Monotonic token guarding against out-of-order responses — filter changes,
// search and pagination each fire an independent request with no cancellation.
let scheduleFetchToken = 0

const fetchSchedules = async () => {
  const token = ++scheduleFetchToken

  isFetchingSchedules.value = true
  await DocumentService.listRetentionSchedules({
    search: searchQuery.value || undefined,
    retention_basis: basisFilter.value || undefined,
    active_only: activeOnly.value || undefined,
    per_page: itemsPerPage.value,
    page: page.value,
  }, data => {
    if (token !== scheduleFetchToken) return
    schedules.value = data?.data ?? []
    schedulesTotal.value = data?.meta?.total ?? 0
  }, () => {
    if (token !== scheduleFetchToken) return
    schedules.value = []
    schedulesTotal.value = 0
  })
  if (token === scheduleFetchToken) isFetchingSchedules.value = false
}

watch([page, itemsPerPage, searchQuery, basisFilter, activeOnly], fetchSchedules)

// Unpaginated (well, generously paged) list for the "filter by schedule"
// dropdown on the destruction log — the main `schedules` ref only ever holds
// the CURRENT page of the schedules table (15 rows), which would silently
// hide most schedules from that filter once more than a page exists.
const scheduleOptions = ref([])

const fetchScheduleOptions = async () => {
  await DocumentService.listRetentionSchedules({ per_page: 100 }, data => {
    scheduleOptions.value = data?.data ?? []
  })
}

// ── Destruction log ────────────────────────────────────
const events = ref([])
const eventsTotal = ref(0)
const isFetchingEvents = ref(false)
const eventsPage = ref(1)
const eventsItemsPerPage = ref(15)
const eventsScheduleFilter = ref(null)

const eventHeaders = [
  { title: 'Record Description', key: 'record_description', sortable: false },
  { title: 'Schedule', key: 'retention_schedule.record_type', sortable: false },
  { title: 'Period Covered', key: 'period', sortable: false },
  { title: 'Quantity', key: 'quantity', sortable: false },
  { title: 'Method', key: 'method', sortable: false },
  { title: 'Destroyed At', key: 'destroyed_at', sortable: false },
  { title: 'Authorised / Executed / Witnessed', key: 'people', sortable: false },
  { title: 'Certificate', key: 'certificate_url', sortable: false },
]

let eventFetchToken = 0

const fetchEvents = async () => {
  const token = ++eventFetchToken

  isFetchingEvents.value = true
  await DocumentService.listDestructionEvents({
    retention_schedule_id: eventsScheduleFilter.value || undefined,
    per_page: eventsItemsPerPage.value,
    page: eventsPage.value,
  }, data => {
    if (token !== eventFetchToken) return
    events.value = data?.data ?? []
    eventsTotal.value = data?.meta?.total ?? 0
  }, () => {
    if (token !== eventFetchToken) return
    events.value = []
    eventsTotal.value = 0
  })
  if (token === eventFetchToken) isFetchingEvents.value = false
}

watch([eventsPage, eventsItemsPerPage, eventsScheduleFilter], fetchEvents)

const refreshAll = () => {
  fetchSchedules()
  fetchGaps()
  fetchEvents()
  fetchScheduleOptions()
}

fetchSchedules()
fetchGaps()
fetchEvents()
fetchScheduleOptions()

// ── Schedule drawer ────────────────────────────────────
const isScheduleDrawerOpen = ref(false)
const editingSchedule = ref(null)

const openCreateSchedule = () => {
  editingSchedule.value = null
  isScheduleDrawerOpen.value = true
}

const openEditSchedule = schedule => {
  editingSchedule.value = schedule
  isScheduleDrawerOpen.value = true
}

const onScheduleSaved = () => {
  isScheduleDrawerOpen.value = false
  refreshAll()
}

// ── Destruction event drawer ───────────────────────────
const isEventDrawerOpen = ref(false)
const eventScheduleContext = ref(null)

const openRecordDestruction = (schedule = null) => {
  eventScheduleContext.value = schedule
  isEventDrawerOpen.value = true
}

const onEventSaved = () => {
  isEventDrawerOpen.value = false
  refreshAll()
}

// ── Delete ──────────────────────────────────────────────
const isConfirmDeleteOpen = ref(false)
const deleteTarget = ref(null)

const requestDelete = schedule => {
  deleteTarget.value = schedule
  isConfirmDeleteOpen.value = true
}

const confirmDelete = async confirmed => {
  if (!confirmed || !deleteTarget.value?.id) return

  await DocumentService.deleteRetentionSchedule(deleteTarget.value.id, () => {
    refreshAll()
  })
}
</script>

<template>
  <div>
    <div class="d-flex align-center flex-wrap gap-4 mb-6">
      <div class="text-h5 flex-grow-1">
        Retention &amp; Destruction
      </div>
      <VBtn
        v-if="canRecordDestruction"
        variant="tonal"
        prepend-icon="tabler-file-certificate"
        @click="openRecordDestruction(null)"
      >
        Record Destruction
      </VBtn>
      <VBtn
        v-if="canManage"
        prepend-icon="tabler-plus"
        @click="openCreateSchedule"
      >
        Add Schedule
      </VBtn>
    </div>

    <!-- 👉 Gap banner -->
    <VAlert
      v-if="gaps.length"
      type="warning"
      variant="tonal"
      class="mb-6"
    >
      <div class="d-flex align-center flex-wrap gap-3 mb-3">
        <div class="text-subtitle-1 font-weight-medium flex-grow-1">
          {{ gaps.length }} active schedule{{ gaps.length === 1 ? '' : 's' }} with no destruction event
          in the last {{ gapYears }} year{{ gapYears === 1 ? '' : 's' }}
        </div>
        <VBtnToggle
          v-model="gapYears"
          density="compact"
          variant="outlined"
          divided
          mandatory
        >
          <VBtn
            :value="1"
            size="small"
          >
            1y
          </VBtn>
          <VBtn
            :value="2"
            size="small"
          >
            2y
          </VBtn>
          <VBtn
            :value="3"
            size="small"
          >
            3y
          </VBtn>
        </VBtnToggle>
      </div>
      <div class="d-flex flex-wrap gap-2">
        <VChip
          v-for="schedule in gaps"
          :key="schedule.id"
          size="small"
          label
        >
          <span>{{ schedule.record_type }}</span>
        </VChip>
      </div>
    </VAlert>

    <!-- 👉 Schedules -->
    <VCard
      variant="flat"
      class="border mb-6"
    >
      <VCardItem>
        <template #title>
          <span class="text-h6 font-weight-medium">Schedules</span>
        </template>
      </VCardItem>

      <VCardText class="d-flex align-center flex-wrap gap-3 pt-0">
        <AppTextField
          placeholder="Search record type..."
          density="compact"
          style="inline-size: 240px;"
          @input="debouncedSearch($event.target.value)"
        />
        <AppSelect
          v-model="basisFilter"
          :items="basisOptions"
          placeholder="Basis"
          density="compact"
          clearable
          style="inline-size: 170px;"
        />
        <VCheckbox
          v-model="activeOnly"
          label="Active only"
          density="compact"
          hide-details
        />
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :headers="scheduleHeaders"
        :items="schedules"
        :items-length="schedulesTotal"
        :loading="isFetchingSchedules"
        class="text-no-wrap"
      >
        <template #item.retention_period_months="{ item }">
          <span>{{ item.retention_period_months != null ? `${item.retention_period_months} mo` : '—' }}</span>
        </template>

        <template #item.retention_basis="{ item }">
          <VChip
            :color="retentionBasisColor(item.retention_basis)"
            size="small"
            label
          >
            <span>{{ retentionBasisTitle(item.retention_basis) }}</span>
          </VChip>
        </template>

        <template #item.trigger_event="{ item }">
          <span>{{ retentionTriggerTitle(item.trigger_event) }}</span>
        </template>

        <template #item.disposition="{ item }">
          <VChip
            :color="retentionDispositionColor(item.disposition)"
            size="small"
            label
          >
            <span>{{ retentionDispositionTitle(item.disposition) }}</span>
          </VChip>
        </template>

        <template #item.owner.name="{ item }">
          <span>{{ item.owner?.name ?? '—' }}</span>
        </template>

        <template #item.destruction_events_count="{ item }">
          <span>{{ item.destruction_events_count ?? 0 }}</span>
        </template>

        <template #item.actions="{ item }">
          <div class="d-flex flex-column align-end">
            <div class="d-flex align-center">
              <IconBtn
                v-if="canRecordDestruction"
                @click="openRecordDestruction(item)"
              >
                <VIcon icon="tabler-file-certificate" />
                <VTooltip
                  activator="parent"
                  location="top"
                >
                  Record destruction
                </VTooltip>
              </IconBtn>
              <IconBtn
                v-if="canManage"
                @click="openEditSchedule(item)"
              >
                <VIcon icon="tabler-edit" />
              </IconBtn>
              <IconBtn
                v-if="canManage"
                :disabled="item.destruction_events_count > 0"
                @click="requestDelete(item)"
              >
                <VIcon icon="tabler-trash" />
              </IconBtn>
            </div>

            <!--
              A VTooltip on a :disabled control never fires — browsers do not
              emit hover events on disabled form controls. A visible caption
              is the only reliable way to explain the refusal.
            -->
            <span
              v-if="canManage && item.destruction_events_count > 0"
              class="text-caption text-medium-emphasis"
            >
              Has destruction events — cannot be deleted
            </span>
          </div>
        </template>

        <template #bottom>
          <VDivider />
          <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 pa-5 pt-3">
            <p class="text-sm text-medium-emphasis mb-0">
              {{ schedulesTotal }} schedules
            </p>
            <VPagination
              v-model="page"
              :length="Math.ceil(schedulesTotal / itemsPerPage) || 1"
              :total-visible="5"
            />
          </div>
        </template>
      </VDataTableServer>
    </VCard>

    <!-- 👉 Destruction log -->
    <VCard
      variant="flat"
      class="border"
    >
      <VCardItem>
        <template #title>
          <span class="text-h6 font-weight-medium">Destruction Log</span>
        </template>
        <template #subtitle>
          <span class="text-body-2 text-medium-emphasis">Append-only. A correction is a new event with remarks, never an edit.</span>
        </template>
      </VCardItem>

      <VCardText class="d-flex align-center flex-wrap gap-3 pt-0">
        <AppSelect
          v-model="eventsScheduleFilter"
          :items="scheduleOptions"
          item-title="record_type"
          item-value="id"
          placeholder="Filter by schedule"
          density="compact"
          clearable
          style="inline-size: 240px;"
        />
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="eventsItemsPerPage"
        v-model:page="eventsPage"
        :headers="eventHeaders"
        :items="events"
        :items-length="eventsTotal"
        :loading="isFetchingEvents"
        class="text-no-wrap"
      >
        <template #item.retention_schedule.record_type="{ item }">
          <span>{{ item.retention_schedule?.record_type ?? '—' }}</span>
        </template>

        <template #item.period="{ item }">
          <span>{{ formatDate(item.period_covered_from) }} – {{ formatDate(item.period_covered_to) }}</span>
        </template>

        <template #item.quantity="{ item }">
          <span>{{ item.quantity != null ? `${item.quantity} ${item.quantity_unit ?? ''}` : '—' }}</span>
        </template>

        <template #item.method="{ item }">
          <span>{{ destructionMethodTitle(item.method) }}</span>
        </template>

        <template #item.destroyed_at="{ item }">
          <span>{{ formatDate(item.destroyed_at) }}</span>
        </template>

        <template #item.people="{ item }">
          <div class="text-body-2">
            <div>Authorised: {{ item.authorised_by?.name ?? '—' }}</div>
            <div>Executed: {{ item.executed_by?.name ?? '—' }}</div>
            <div v-if="item.witnessed_by">
              Witnessed: {{ item.witnessed_by.name }}
            </div>
          </div>
        </template>

        <template #item.certificate_url="{ item }">
          <a
            v-if="item.certificate_url"
            :href="item.certificate_url"
            target="_blank"
            rel="noopener noreferrer"
          >
            View
          </a>
          <span v-else>—</span>
        </template>

        <template #bottom>
          <VDivider />
          <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 pa-5 pt-3">
            <p class="text-sm text-medium-emphasis mb-0">
              {{ eventsTotal }} destruction events
            </p>
            <VPagination
              v-model="eventsPage"
              :length="Math.ceil(eventsTotal / eventsItemsPerPage) || 1"
              :total-visible="5"
            />
          </div>
        </template>
      </VDataTableServer>
    </VCard>

    <RetentionScheduleDrawer
      v-model:is-drawer-open="isScheduleDrawerOpen"
      :schedule="editingSchedule"
      @saved="onScheduleSaved"
    />

    <DestructionEventDrawer
      v-model:is-drawer-open="isEventDrawerOpen"
      :schedule="eventScheduleContext"
      @saved="onEventSaved"
    />

    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmDeleteOpen"
      confirmation-question="Are you sure you want to delete this retention schedule?"
      confirm-title="Delete Schedule"
      confirm-msg="Retention schedule has been deleted."
      confirm-btn-color="error"
      @confirm="confirmDelete"
    />
  </div>
</template>
